import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import {get, ref, serverTimestamp, update} from 'firebase/database';
import {database} from '../../services/firebase';
import {useAuth} from '../../contexts/AuthContext';
import Header from '../../components/common/Header';

export default function AdminDashboard() {
    const {userRole} = useAuth();
    const [loading, setLoading] = useState(true);
    const [quizzes, setQuizzes] = useState([]);
    const [stats, setStats] = useState(null);
    const [gameState, setGameState] = useState({active: true});
    const [error, setError] = useState('');

    useEffect(() => {
        async function fetchData() {
            try {
                if (userRole !== 'admin') {
                    setError('Accès non autorisé. Vous devez être administrateur pour voir cette page.');
                    setLoading(false);
                    return;
                }

                // Récupérer l'état global du jeu
                const gameStateRef = ref(database, 'gameState');
                const gameStateSnapshot = await get(gameStateRef);
                const gameStateData = gameStateSnapshot.val() || {active: true, activeQuizzes: {}};
                setGameState(gameStateData);

                // Récupérer les quiz
                const quizzesRef = ref(database, 'quizzes');
                const quizzesSnapshot = await get(quizzesRef);

                if (quizzesSnapshot.exists()) {
                    const quizzesData = quizzesSnapshot.val();
                    const processedQuizzes = Object.entries(quizzesData).map(([id, data]) => ({
                        id,
                        ...data.metadata,
                        active: data.metadata?.active && (gameStateData.active || (gameStateData.activeQuizzes && gameStateData.activeQuizzes[id])),
                        totalQuestions: data.questions ? data.questions.length : 0
                    }));

                    setQuizzes(processedQuizzes);
                }

                // Récupérer les statistiques globales
                // Nombre total de joueurs
                const usersRef = ref(database, 'users');
                const usersSnapshot = await get(usersRef);
                const totalUsers = usersSnapshot.exists() ? Object.keys(usersSnapshot.val()).length : 0;

                // Nombre total de questions
                const questionBankRef = ref(database, 'questionBank');
                const questionBankSnapshot = await get(questionBankRef);
                const totalQuestions = questionBankSnapshot.exists() ? Object.keys(questionBankSnapshot.val()).length : 0;

                // Nombre total de sessions de jeu
                const gameResultsRef = ref(database, 'gameResults');
                const gameResultsSnapshot = await get(gameResultsRef);
                let totalGameSessions = 0;

                if (gameResultsSnapshot.exists()) {
                    const gameResults = gameResultsSnapshot.val();
                    Object.values(gameResults).forEach(quizSessions => {
                        totalGameSessions += Object.keys(quizSessions).length;
                    });
                }

                setStats({
                    totalUsers,
                    totalQuizzes: quizzes.length,
                    totalQuestions,
                    totalGameSessions
                });

                setLoading(false);
            } catch (error) {
                console.error("Erreur lors de la récupération des données:", error);
                setError("Une erreur s'est produite lors du chargement des données.");
                setLoading(false);
            }
        }

        fetchData();
    }, [userRole, quizzes]);

    // Activer/désactiver un quiz
    const toggleQuizActive = async (quizId, currentActive) => {
        try {
            const quizRef = ref(database, `quizzes/${quizId}/metadata`);
            await update(quizRef, {
                active: !currentActive,
                updatedAt: serverTimestamp()
            });

            // Mettre à jour l'état local
            setQuizzes(prevQuizzes =>
                prevQuizzes.map(quiz =>
                    quiz.id === quizId
                        ? {...quiz, active: !currentActive}
                        : quiz
                )
            );

        } catch (error) {
            console.error("Erreur lors de la mise à jour du quiz:", error);
            alert("Erreur lors de la mise à jour du quiz. Veuillez réessayer.");
        }
    };

    // Activer/désactiver tous les quiz
    const toggleAllQuizzes = async () => {
        try {
            const updatedGameState = {
                ...gameState,
                active: !gameState.active,
                updatedAt: serverTimestamp()
            };

            const gameStateRef = ref(database, 'gameState');
            await update(gameStateRef, updatedGameState);

            setGameState(updatedGameState);

            // Mettre à jour l'affichage des quiz
            setQuizzes(prevQuizzes =>
                prevQuizzes.map(quiz => ({
                    ...quiz,
                    active: quiz.metadata?.active && updatedGameState.active
                }))
            );

        } catch (error) {
            console.error("Erreur lors de la mise à jour de l'état du jeu:", error);
            alert("Erreur lors de la mise à jour de l'état du jeu. Veuillez réessayer.");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-indigo-100">
                <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-6">
                    <div className="text-center">
                        <svg className="mx-auto h-12 w-12 text-red-500" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                        </svg>
                        <h3 className="mt-2 text-lg font-medium text-gray-900">Erreur</h3>
                        <p className="mt-1 text-sm text-gray-500">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            <Header title="Dashboard Administrateur"/>

            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                {/* Statistiques */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Statistiques</h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-4">
                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"/>
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total
                                                Utilisateurs
                                            </dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {stats?.totalUsers || 0}
                                                </div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Quiz</dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {quizzes.length}
                                                </div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Total Questions
                                            </dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {stats?.totalQuestions || 0}
                                                </div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 bg-indigo-500 rounded-md p-3">
                                        <svg className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24"
                                             stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"/>
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                  d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z"/>
                                        </svg>
                                    </div>
                                    <div className="ml-5 w-0 flex-1">
                                        <dl>
                                            <dt className="text-sm font-medium text-gray-500 truncate">Sessions de jeu
                                            </dt>
                                            <dd className="flex items-baseline">
                                                <div className="text-2xl font-semibold text-gray-900">
                                                    {stats?.totalGameSessions || 0}
                                                </div>
                                            </dd>
                                        </dl>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* État du jeu */}
                <div className="mb-8">
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-lg font-medium text-gray-900">État Global</h2>
                            <div className="flex space-x-3">
                <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${
                    gameState.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {gameState.active ? 'Actif' : 'Inactif'}
                </span>
                                <button
                                    type="button"
                                    onClick={toggleAllQuizzes}
                                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                                        gameState.active
                                            ? 'bg-red-600 hover:bg-red-700'
                                            : 'bg-green-600 hover:bg-green-700'
                                    } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`}
                                >
                                    {gameState.active ? 'Désactiver tous les quiz' : 'Activer tous les quiz'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Gestion des quiz */}
                <div className="mb-8">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-medium text-gray-900">Gestion des Quiz</h2>
                        <Link
                            to="/admin/quiz/new"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                            Créer un nouveau quiz
                        </Link>
                    </div>

                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Titre
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Catégorie
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Difficulté
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Questions
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Temps/Question
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        État
                                    </th>
                                    <th scope="col"
                                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                {quizzes.length > 0 ? (
                                    quizzes.map((quiz) => (
                                        <tr key={quiz.id}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {quiz.title}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {quiz.category || 'N/A'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              quiz.difficulty === 'easy'
                                  ? 'bg-green-100 text-green-800'
                                  : quiz.difficulty === 'medium'
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                          }`}>
                            {quiz.difficulty === 'easy'
                                ? 'Facile'
                                : quiz.difficulty === 'medium'
                                    ? 'Moyen'
                                    : 'Difficile'
                            }
                          </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {quiz.totalQuestions}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {quiz.timePerQuestion} sec
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              quiz.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {quiz.active ? 'Actif' : 'Inactif'}
                          </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="flex space-x-3">
                                                    <Link
                                                        to={`/admin/quiz/${quiz.id}`}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                    >
                                                        Éditer
                                                    </Link>
                                                    <button
                                                        onClick={() => toggleQuizActive(quiz.id, quiz.active)}
                                                        className={`${
                                                            quiz.active ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                                                        }`}
                                                    >
                                                        {quiz.active ? 'Désactiver' : 'Activer'}
                                                    </button>
                                                    <Link
                                                        to={`/admin/quiz/${quiz.id}/results`}
                                                        className="text-gray-600 hover:text-gray-900"
                                                    >
                                                        Résultats
                                                    </Link>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                            Aucun quiz disponible. Créez votre premier quiz !
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Actions rapides */}
                <div className="mb-8">
                    <h2 className="text-lg font-medium text-gray-900 mb-4">Actions rapides</h2>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Gestion des questions</h3>
                                <div className="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>Créez et gérez la banque de questions pour les quiz et les sessions
                                        d'entraînement.</p>
                                </div>
                                <div className="mt-5">
                                    <Link
                                        to="/admin/questions"
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Gérer les questions
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Statistiques des
                                    joueurs</h3>
                                <div className="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>Consultez les statistiques détaillées des joueurs et leurs performances.</p>
                                </div>
                                <div className="mt-5">
                                    <Link
                                        to="/admin/players"
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Voir les statistiques
                                    </Link>
                                </div>
                            </div>
                        </div>

                        <div className="bg-white overflow-hidden shadow rounded-lg">
                            <div className="px-4 py-5 sm:p-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900">Annonces</h3>
                                <div className="mt-2 max-w-xl text-sm text-gray-500">
                                    <p>Créez et gérez les annonces pour informer les joueurs des nouveautés et mises à
                                        jour.</p>
                                </div>
                                <div className="mt-5">
                                    <Link
                                        to="/admin/announcements"
                                        className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        Gérer les annonces
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
}
